import { trans } from "@mongez/localization";
import {
  cartAtom,
  useAddToCart,
  useRemoveFromCart,
  useUpdateQuantity,
} from "core/cart";
import { Product, ShopProduct } from "core/catalog";
import { cn } from "core/shared";
import { userAtom } from "core/user";
import { useEffect, useState } from "react";
import {
  AddToCart,
  Button,
  Minus,
  Plus,
  Trash,
} from "~/design-system/components";
import { errorToast, successToast } from "~/design-system/components/toast";
import { triggerCartRemoved, triggerCartUpdated } from "~/modules/cart/events";

type AddToCartButtonProps = {
  product: Product | ShopProduct;
};

export function AddToCartButton({ product }: AddToCartButtonProps) {
  const cart = cartAtom.useValue();

  const productInCartId = cart?.items?.find(
    (item) => item.product.id === product.id,
  )?.id;

  const [quantity, setQuantity] = useState(product.cartItem?.quantity || 0);

  const { updateQuantity, loading: updateQuantityLoading } = useUpdateQuantity({
    onSuccess: (data) => {
      successToast(trans("cartUpdatedSuccessfully"));
      triggerCartUpdated({
        cart: data.cart,
        product,
      });

      const cartItem = data.cart.items.find(
        (item) => item.product.id === product.id,
      );

      if (cartItem) {
        product.cartItem = cartItem;
      }

      userAtom.update(data.user);
    },
    onError: (response: any) => errorToast(response),
  });

  const { addToCart, loading } = useAddToCart({
    onSuccess: (data) => {
      triggerCartUpdated({
        cart: data.cart,
        product,
      });

      const cartItem = data.cart.items.find(
        (item) => item.product.id === product.id,
      );

      if (cartItem) {
        product.cartItem = cartItem;
      }

      cartAtom.update(data.cart);

      successToast(trans("addedToCart"));
      userAtom.update(data.user);
    },
    onError: (response: any) => errorToast(response),
  });

  const { removeFromCart } = useRemoveFromCart({
    onSuccess: (data) => {
      successToast(trans("removedFromCart"));
      triggerCartRemoved({
        cart: data.cart,
        product,
      });
      userAtom.update(data.user);
    },
    onError: (error: any) => errorToast(error),
  });

  useEffect(() => {
    if (product.cartItem && product.cartItem.quantity) {
      setQuantity(product.cartItem.quantity);
    } else {
      setQuantity(0);
    }
  }, [product.cartItem]);

  if (!product.inStock) {
    return null;
  }

  /**
   * Handles updating the product quantity in the cart.
   * Calls the updateQuantity hook and sets the internal quantity state.
   *
   * @param newQuantity - The new quantity to set for this product in the cart.
   */
  const handleUpdateQuantity = async (newQuantity: number) => {
    const updatedQuantity = await updateQuantity(
      productInCartId ? productInCartId : (product.cartItem?.id as number),
      newQuantity,
    );
    if (updatedQuantity) {
      setQuantity(updatedQuantity);
    }
  };

  /**
   * Increments the cart quantity for this product by 1,
   * unless the maximum available stock is reached.
   * Shows an error toast if the user tries to add more than available stock.
   */
  const handleIncrementQuantity = async () => {
    if (!product.stock.available || quantity >= product.stock.available)
      return errorToast(
        `${trans("youCanNotAddMoreThan")} ${product.stock.available} ${trans("items")}`,
      );

    await handleUpdateQuantity(quantity + 1);
  };

  /**
   * Decrements the cart quantity for this product by 1.
   * If the quantity is 1, removing it will delete the item from cart instead.
   * Calls removeFromCart if necessary, otherwise updates quantity.
   */
  const handleDecrementQuantity = async () => {
    if (quantity === 1) {
      await removeFromCart(product.cartItem?.id as number);
      return setQuantity(0);
    }

    await handleUpdateQuantity(quantity - 1);
  };

  if (quantity) {
    return (
      <div
        className={cn(
          "hover:bg-primary-light absolute right-2 bottom-2 z-20 items-center justify-center rounded-xl border bg-white p-1",
          quantity >= 1 &&
            "bg-primary-default hover:bg-primary-default border-black text-white",
        )}
        title={trans("addToCart")}
        aria-label={trans("addToCart")}
      >
        <div className="flex items-center gap-4">
          <Button
            onClick={handleDecrementQuantity}
            disabled={updateQuantityLoading || loading}
            className="rounded-lg p-1 hover:bg-white hover:text-red-500"
          >
            {quantity > 1 ? (
              <Minus className="size-5" />
            ) : (
              <Trash className="size-6" />
            )}
          </Button>
          <span className="mb-1 text-2xl font-medium">
            {updateQuantityLoading ? "..." : quantity}
          </span>
          <Button
            onClick={handleIncrementQuantity}
            disabled={updateQuantityLoading || loading}
            className="rounded-lg p-1 hover:bg-white hover:text-black"
          >
            <Plus className="size-6" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Button
      className="hover:bg-primary-light absolute right-2 bottom-2 z-20 items-center justify-center rounded-xl border bg-white p-2"
      title={trans("addToCart")}
      aria-label={trans("addToCart")}
      disabled={loading}
      onClick={async (e) => {
        e.stopPropagation();
        let quantity = 1;

        if (product.purchase?.minQuantity) {
          quantity = product.purchase.minQuantity;
        }

        setQuantity(quantity);
        await addToCart(product.id, quantity);
      }}
    >
      <AddToCart className="size-7" />
    </Button>
  );
}
